import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackPixelEvent } from '../../components/FacebookPixelTracker';
import type { DashboardState } from './useDashboardState';

export const useUrlParams = (state: DashboardState) => {
  const location = useLocation();
  const navigate = useNavigate();
  const processedSearchRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip if we've already processed these exact params
    if (!location.search || processedSearchRef.current === location.search) {
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    const isSetupComplete = urlParams.get('setup') === 'complete';
    const isSuccess = urlParams.get('success') === 'true';
    const isDowngraded = urlParams.get('downgraded') === 'true';
    const cancelDate = urlParams.get('cancelDate');
    const customMessage = urlParams.get('message');

    let shouldCleanUrl = false;

    if (customMessage) {
      state.setSuccessMessage(customMessage);
      state.setShowSuccessToast(true);
      setTimeout(() => state.setShowSuccessToast(false), 5000);
      shouldCleanUrl = true;
    } else if (isSetupComplete) {
      state.setSuccessMessage("Payment method saved. You're on the Free plan — $0 charged today.");
      state.setShowSuccessToast(true);
      setTimeout(() => state.setShowSuccessToast(false), 5000);
      shouldCleanUrl = true;
    } else if (isSuccess) {
      trackPixelEvent('Purchase', { currency: 'USD' });
      const planDisplayName = urlParams.get('planDisplayName');
      state.setSuccessMessage(
        planDisplayName
          ? `Congratulations! Your ${planDisplayName} subscription is active.`
          : 'Subscription activated successfully! You can now access all features of your plan.'
      );
      state.setShowSuccessToast(true);
      setTimeout(() => state.setShowSuccessToast(false), 5000);
      shouldCleanUrl = true;
    } else if (isDowngraded) {
      const message = cancelDate
        ? `Your subscription has been cancelled. You will continue to have access to paid features until ${cancelDate}.`
        : 'Your subscription has been cancelled. You will continue to have access to paid features until the end of your billing period.';
      state.setSuccessMessage(message);
      state.setShowSuccessToast(true);
      setTimeout(() => state.setShowSuccessToast(false), 7000);
      shouldCleanUrl = true;
    }

    // Mark these params as processed before cleaning the URL
    processedSearchRef.current = location.search;

    if (shouldCleanUrl) {
      // Use React Router's navigate to update both the browser URL and
      // React Router's internal location state (unlike window.history.replaceState
      // which only updates the browser URL)
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate, state]);
};
